/**
 * Subscribe ONCE to all calibrations + measurements for a document and surface
 * the slice that matches the current page. Filtering in memory avoids spinning
 * up a fresh Firestore listener on every page change (the viewer flips pages
 * often via the thumbnail rail / pager, so per-page subscriptions would be
 * needlessly chatty and rack up read costs).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  subscribeDocumentCalibrations,
  subscribeDocumentMeasurements,
} from '../utils/pdfMeasurementService';
import type { PdfMeasurement, PdfMeasurementCalibration } from '../types';

export interface UsePdfMeasurementsResult {
  /** Calibration for the *current* page (or null if none set yet). */
  calibration: PdfMeasurementCalibration | null;
  /** Measurements scoped to the current page. */
  measurements: PdfMeasurement[];
  /** All measurements for the document. */
  allMeasurements: PdfMeasurement[];
  /** All calibrations for the document — used to know how many pages have a scale. */
  allCalibrations: PdfMeasurementCalibration[];
  loading: boolean;
}

export function usePdfMeasurements(documentId: string | null, pageNumber: number): UsePdfMeasurementsResult {
  const [allCalibrations, setAllCalibrations] = useState<PdfMeasurementCalibration[]>([]);
  const [allMeasurements, setAllMeasurements] = useState<PdfMeasurement[]>([]);
  const [calibLoaded, setCalibLoaded] = useState(false);
  const [measLoaded, setMeasLoaded] = useState(false);

  useEffect(() => {
    if (!documentId) {
      setAllCalibrations([]);
      setCalibLoaded(true);
      return;
    }
    setCalibLoaded(false);
    const unsub = subscribeDocumentCalibrations(documentId, list => {
      setAllCalibrations(list);
      setCalibLoaded(true);
    });
    return () => unsub();
  }, [documentId]);

  useEffect(() => {
    if (!documentId) {
      setAllMeasurements([]);
      setMeasLoaded(true);
      return;
    }
    setMeasLoaded(false);
    const unsub = subscribeDocumentMeasurements(documentId, list => {
      setAllMeasurements(list);
      setMeasLoaded(true);
    });
    return () => unsub();
  }, [documentId]);

  const calibration = useMemo<PdfMeasurementCalibration | null>(() => {
    const matches = allCalibrations.filter(c => c.pageNumber === pageNumber);
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    // Defensive: prefer most recently updated/created if duplicates exist.
    const sorted = matches.slice().sort((a, b) =>
      (b.updatedAt?.getTime() || b.createdAt.getTime()) - (a.updatedAt?.getTime() || a.createdAt.getTime())
    );
    return sorted[0];
  }, [allCalibrations, pageNumber]);

  const measurements = useMemo(
    () => allMeasurements.filter(m => m.pageNumber === pageNumber),
    [allMeasurements, pageNumber],
  );

  return {
    calibration,
    measurements,
    allMeasurements,
    allCalibrations,
    loading: !calibLoaded || !measLoaded,
  };
}
